import { useState, useEffect, useCallback } from 'react';
import { assessmentService } from '@/services';
import { useCategories } from './useCategory';
import { useDifficulty } from './useDifficulty';
import type {
  Question,
  CreateQuestionRequest,
  Assessment,
  CreateAssessmentRequest,
  AssessmentAttempt,
  SubmitAnswerRequest,
  AssessmentAnalytics,
  QueryParams,
  UserAttemptHistory,
} from '@/types/assessment';
import toast from 'react-hot-toast';

// Hook for Question Management
export const useQuestions = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const fetchQuestions = useCallback(async (params?: QueryParams, assessmentId?: string) => {
    try {
      setLoading(true);
      setError(null);
      if(!assessmentId){
        return
      }
      // Use assessment-specific endpoint if assessmentId is provided
           const response = assessmentId 
        ? await assessmentService.getAssessmentQuestions(assessmentId, params)
        : await assessmentService.getQuestions(params);
      // const response = assessmentId 
      //   ? await assessmentService.getAssessmentQuestions(assessmentId, params)
      //   : await assessmentService.getQuestions(params);
      
      if (response.success && response.data) {
        // Handle the paginated response structure
        const data = response.data as any;
        if (data.questions && Array.isArray(data.questions)) {
          setQuestions(data.questions);
          setPagination({
            totalItems: data.pagination?.totalQuestions || data.totalItems || 0,
            totalPages: data.pagination?.totalPages || data.totalPages || 1,
            currentPage: data.pagination?.currentPage || data.currentPage || 1,
            hasNextPage: data.pagination?.hasNext || data.hasNextPage || false,
            hasPrevPage: data.pagination?.hasPrev || data.hasPrevPage || false,
          });
        } else if (Array.isArray(data)) {
          // Direct array response
          setQuestions(data);
          setPagination({
            totalItems: data.length,
            totalPages: 1,
            currentPage: 1,
            hasNextPage: false,
            hasPrevPage: false,
          });
        } else {
          // Empty fallback
          setQuestions([]);
          setPagination({
            totalItems: 0,
            totalPages: 1,
            currentPage: 1,
            hasNextPage: false,
            hasPrevPage: false,
          });
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch questions');
      console.error('Failed to fetch questions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createQuestion = useCallback(async (questionData: CreateQuestionRequest, assessmentId?: string) => {
    try {
      // Use assessment-specific endpoint if assessmentId is provided
      const response = assessmentId 
        ? await assessmentService.createAssessmentQuestion(assessmentId, questionData)
        : await assessmentService.createQuestion(questionData);
        
      if (response.success) {
        toast.success('Question created successfully');
        return response.data?.question;
      }
    } catch (err: any) {
      console.log(err)
      toast.error(err.message || 'Failed to create question');
      throw err;
    }
  }, []);

  const updateQuestion = useCallback(async (id: string, questionData: CreateQuestionRequest) => {
    try {
      const response = await assessmentService.updateQuestion(id, questionData);
      if (response.success) {
        toast.success('Question updated successfully');
        return response.data?.question;
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to update question');
      throw err;
    }
  }, []);

  const deleteQuestion = useCallback(async (id: string) => {
    try {
      const response = await assessmentService.deleteQuestion(id);
      if (response.success) {
        toast.success('Question deleted successfully');
        setQuestions(prev => prev.filter(q => q._id !== id));
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete question');
      throw err;
    }
  }, []);

  const bulkCreateQuestions = useCallback(async (questions: CreateQuestionRequest[]) => {
    try {
      const response = await assessmentService.bulkCreateQuestions(questions);
      if (response.success) {
        toast.success(`${response.data?.questions.length} questions created successfully`);
        return response.data?.questions;
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to create questions');
      throw err;
    }
  }, []);

  const importFromExcel = useCallback(async (file: File) => {
    try {
      const response = await assessmentService.importQuestionsFromExcel(file);
      if (response.success) {
        toast.success(`${response.data?.importedCount} questions imported successfully`);
        return response.data;
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to import questions');
      throw err;
    }
  }, []);

  const downloadTemplate = useCallback(async () => {
    try {
      const blob = await assessmentService.downloadSampleExcel();
      assessmentService.downloadFile(blob, 'questions_template.xlsx');
      toast.success('Template downloaded successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to download template');
    }
  }, []);

  const getQuestion = useCallback(async (id: string) => {
    try {
      const response = await assessmentService.getQuestion(id);
      if (response.success && response.data) {
        return response.data.question;
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to fetch question');
      throw err;
    }
  }, []);

  return {
    questions,
    loading,
    error,
    pagination,
    fetchQuestions,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    bulkCreateQuestions,
    importFromExcel,
    downloadTemplate,
    getQuestion,
  };
};

// Hook for Assessment Management
export const useAssessments = () => {
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const fetchAssessments = useCallback(async (params?: QueryParams) => {
    try {
      setLoading(true);
      setError(null);
      const response = await assessmentService.getAssessments(params);
      
      if (response.success && response.data) {
        // Handle the paginated response structure
        const data = response.data as any;
        if (data.assessments && Array.isArray(data.assessments)) {
          setAssessments(data.assessments);
          setPagination({
            totalItems: data.pagination?.totalAssessments || data.totalItems || 0,
            totalPages: data.pagination?.totalPages || data.totalPages || 1,
            currentPage: data.pagination?.currentPage || data.currentPage || 1,
            hasNextPage: data.pagination?.hasNext || data.hasNextPage || false,
            hasPrevPage: data.pagination?.hasPrev || data.hasPrevPage || false,
          });
        } else if (Array.isArray(data)) {
          // Direct array response
          setAssessments(data);
          setPagination({
            totalItems: data.length,
            totalPages: 1,
            currentPage: 1,
            hasNextPage: false,
            hasPrevPage: false,
          });
        } else {
          // Empty fallback
          setAssessments([]);
          setPagination({
            totalItems: 0,
            totalPages: 1,
            currentPage: 1,
            hasNextPage: false,
            hasPrevPage: false,
          });
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch assessments');
      console.error('Failed to fetch assessments:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createAssessment = useCallback(async (assessmentData: CreateAssessmentRequest) => {
    try {
      const response = await assessmentService.createAssessment(assessmentData);
      if (response.success) {
        toast.success('Assessment created successfully');
        return response.data?.assessment;
      }
    } catch (err: any) {
      console.log(err?.response?.data?.errors?.length ? err?.response?.data?.errors[0]['message']:"");
      toast.error(err?.response?.data?.errors?.length ? err?.response?.data?.errors[0]['message']:err.message || 'Failed to create assessment');
      throw err;
    }
  }, []);

  const updateAssessment = useCallback(async (id: string, assessmentData: CreateAssessmentRequest) => {
    try {
      const response = await assessmentService.updateAssessment(id, assessmentData);
      if (response.success) {
        toast.success('Assessment updated successfully');
        return response.data?.assessment;
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to update assessment');
      throw err;
    }
  }, []);

  const deleteAssessment = useCallback(async (id: string) => {
    try {
      const response = await assessmentService.deleteAssessment(id);
      if (response.success) {
        toast.success('Assessment deleted successfully');
        setAssessments(prev => prev.filter(a => a._id !== id));
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete assessment');
      throw err;
    }
  }, []);

  const getAssessment = useCallback(async (id: string) => {
    try {
      const response = await assessmentService.getAssessment(id);
      if (response.success && response.data) {
        return response.data.assessment;
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to fetch assessment');
      throw err;
    }
  }, []);

  return {
    assessments,
    loading,
    error,
    pagination,
    fetchAssessments,
    createAssessment,
    updateAssessment,
    deleteAssessment,
    getAssessment,
  };
};

// Hook for Assessment Taking
export const useAssessmentTaking = () => {
  const [currentAttempt, setCurrentAttempt] = useState<AssessmentAttempt | null>(null);
  const [currentQuestions, setCurrentQuestions] = useState<any[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startAssessment = useCallback(async (assessmentId: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await assessmentService.startAssessment(assessmentId);
      
      if (response.success && response.data) {
        setCurrentAttempt(response.data.attempt);
        setCurrentQuestions(response.data.questions);
        setCurrentQuestionIndex(0);
        setAnswers({});
        setTimeRemaining(response.data.attempt.timeRemaining || response.data.attempt.timeLimit * 60);
        toast.success('Assessment started successfully');
        return response.data;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to start assessment');
      toast.error(err.message || 'Failed to start assessment');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const submitAnswer = useCallback(async (answerData: SubmitAnswerRequest) => {
    if (!currentAttempt) return;

    try {
      const response = await assessmentService.submitAnswer(currentAttempt._id, answerData);
      
      if (response.success && response.data) {
        setAnswers(prev => ({
          ...prev,
          [answerData.questionId]: answerData.selectedAnswer,
        }));
        setTimeRemaining(response.data.timeRemaining);
        
        // Move to next question if available
        if (response.data.nextQuestionId) {
          setCurrentQuestionIndex(prev => prev + 1);
        }
        
        return response.data;
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to submit answer');
      throw err;
    }
  }, [currentAttempt]);

  const completeAssessment = useCallback(async () => {
    if (!currentAttempt) return;

    try {
      setLoading(true);
      const response = await assessmentService.completeAssessment(currentAttempt._id);
      
      if (response.success && response.data) {
        setCurrentAttempt(response.data.attempt);
        toast.success('Assessment completed successfully');
        return response.data.attempt;
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to complete assessment');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentAttempt]);

  const resetAssessment = useCallback(() => {
    setCurrentAttempt(null);
    setCurrentQuestions([]);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setTimeRemaining(0);
    setError(null);
  }, []);

  return {
    currentAttempt,
    currentQuestions,
    currentQuestionIndex,
    answers,
    timeRemaining,
    loading,
    error,
    startAssessment,
    submitAnswer,
    completeAssessment,
    resetAssessment,
    setCurrentQuestionIndex,
    setTimeRemaining,
  };
};

// Hook for User Assessment History
export const useUserAttempts = () => {
  const [attempts, setAttempts] = useState<UserAttemptHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const fetchAttempts = useCallback(async (params?: QueryParams) => {
    try {
      setLoading(true);
      setError(null);
      const response = await assessmentService.getMyAttempts(params);
      
      if (response.success && response.data) {
        const data = response.data as any;
        if (data.data && Array.isArray(data.data)) {
          setAttempts(data.data);
        } else if (Array.isArray(data)) {
          setAttempts(data);
        } else {
          setAttempts([]);
        }
        
        setPagination({
          totalItems: data.totalItems || 0,
          totalPages: data.totalPages || 1,
          currentPage: data.currentPage || 1,
          hasNextPage: data.hasNextPage || false,
          hasPrevPage: data.hasPrevPage || false,
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch attempts');
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    attempts,
    loading,
    error,
    pagination,
    fetchAttempts,
  };
};

// Hook for Analytics
export const useAssessmentAnalytics = () => {
  const [analytics, setAnalytics] = useState<AssessmentAnalytics | null>(null);
  const [myAnalytics, setMyAnalytics] = useState<any>(null);
  const [systemAnalytics, setSystemAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAssessmentAnalytics = useCallback(async (assessmentId: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await assessmentService.getAssessmentAnalytics(assessmentId);
      
      if (response.success && response.data) {
        setAnalytics(response.data.analytics);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await assessmentService.getMyAnalytics();
      
      if (response.success && response.data) {
        setMyAnalytics(response.data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch personal analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSystemAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await assessmentService.getSystemAnalytics();
      
      if (response.success && response.data) {
        setSystemAnalytics(response.data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch system analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    analytics,
    myAnalytics,
    systemAnalytics,
    loading,
    error,
    fetchAssessmentAnalytics,
    fetchMyAnalytics,
    fetchSystemAnalytics,
  };
};

// Hook for Assessment Utilities (updated to use new services)
export const useAssessmentUtils = () => {
  // Use the new hooks for categories and difficulties
  const { categories: categoryList, fetchActiveCategories } = useCategories();
  const { difficultyLevels, answerOptionValues, getDifficultyColor: getDiffColor } = useDifficulty();
  
  // Return category objects instead of just names for ID access
  const categories = categoryList.length > 0 ? categoryList : [];  // Remove deprecated fallback
  const answerOptions = answerOptionValues;

  // Fetch categories on mount
  useEffect(() => {
    if (categoryList.length === 0) {
      fetchActiveCategories().catch(() => {
        // Fallback is already handled in the categories assignment above
      });
    }
  }, [categoryList.length, fetchActiveCategories]);

  const formatTimeRemaining = useCallback((seconds: number) => {
    return assessmentService.formatTimeRemaining(seconds);
  }, []);

  const formatTimeSpent = useCallback((seconds: number) => {
    return assessmentService.formatTimeSpent(seconds);
  }, []);

  const getStatusColor = useCallback((status: string) => {
    return assessmentService.getStatusColor(status);
  }, []);

  const getDifficultyColor = useCallback((difficulty: string) => {
    return getDiffColor(difficulty);
  }, [getDiffColor]);

  return {
    categories,
    difficultyLevels,
    answerOptions,
    formatTimeRemaining,
    formatTimeSpent,
    getStatusColor,
    getDifficultyColor,
  };
};